<template>
  <div class="chart-box d-flex flex-column-reverse">
    <div class="overflow-auto disable-scrollbars">
      <apexchart ref="lineChart" width="1060" height=300 v-if="value"
                 :options="options" :series="value.series"></apexchart>
    </div>
    <div v-if="value" class="d-flex order-3 order-lg-2 align-items-start justify-content-center">
      <div :class="['d-flex',
                    'justify-content-center',
                    'serie-button',
                    { 'mr-2': index < value.series.length - 1 },
                    { 'hidden': hiddenCaptions.includes(serie.name) }]"
            v-for="(serie, index) in value.series" :key="index"
            @click="toggleSerie(serie.name)">
        <span class="d-flex align-items-center">{{serie.name}}</span>
      </div>
    </div>
  </div>
</template>

<script>
import {
  localization, colors, font, mobileBreakPoint,
} from '@/utils/chartOptions';

export default {
  name: 'LineChart',
  data() {
    return {
      resizeEnd: null,
      hiddenCaptions: [],
      options: {
        chart: {
          type: 'line',
          defaultLocale: localization.default,
          locales: localization.locales,
          fontFamily: font.FAMILY,
          foreColor: colors.BLACK,
          toolbar: {
            show: false,
          },
          events: {
            mounted: this.handleResize,
          },
        },
        plotOptions: {
          bar: {
            columnWidth: '50%',
          },
        },
        grid: {
          borderColor: colors.SILVER,
          xaxis: {
            lines: {
              show: true,
            },
          },
          yaxis: {
            lines: {
              show: false,
            },
          },
        },
        markers: {
          size: 3,
          strokeWidth: 0,
          hover: {
            size: 4,
          },
        },
        colors: [colors.FLUSH_ORANGE, colors.RAJAH, colors.COPPER, colors.RED],
        tooltip: {
          shared: false,
          marker: {
            show: false,
          },
          x: {
            formatter: this.xAxisTooltipFormatter,
          },
        },
        legend: {
          show: false,
        },
        stroke: {
          lineCap: 'round',
          width: 2,
        },
        xaxis: {
          labels: {
            rotate: -55,
            hideOverlappingLabels: false,
            showDuplicates: true,
            formatter: this.xAxisLabelsFormatter,
          },
          axisBorder: {
            show: true,
            height: 2,
            color: colors.DOVE_GRAY,
          },
          tooltip: {
            enabled: false,
          },
          categories: this.value?.categories,
        },
        yaxis: {
          show: false,
        },
      },
    };
  },
  props: {
    value: {
      type: Object,
      required: true,
      validator(chartData) {
        return chartData
          && Object.prototype.hasOwnProperty.call(chartData, 'categories')
          && Object.prototype.hasOwnProperty.call(chartData, 'series')
          && chartData.series.every(s => Object.prototype.hasOwnProperty.call(s, 'name')
          && Object.prototype.hasOwnProperty.call(s, 'data'));
      },
    },
    initialHiddenCaptions: { type: Array },
  },
  watch: {
    value: 'applyNewChartData',
  },
  created() {
    if (this.initialHiddenCaptions) {
      this.hiddenCaptions = this.initialHiddenCaptions;
    }
  },
  methods: {
    xAxisTooltipFormatter() {
      return this.value.title;
    },
    xAxisLabelsFormatter(val) {
      if (val) {
        const data = new Date(val);
        if (!Number.isNaN(data.getTime())) {
          const dataJson = data.toJSON();
          const dateMatch = dataJson.match(/\d{4}-(\d{2})-(\d{2})/);
          if (dateMatch) {
            return `${dateMatch[2]}/${dateMatch[1]}`;
          }
        }
      }
      return val;
    },
    handleResize() {
      clearTimeout(this.resizeEnd);
      const ctx = this;
      this.resizeEnd = setTimeout(() => {
        const chartOptsCopy = JSON.parse(JSON.stringify(ctx.options.chart));
        if (window.innerWidth < mobileBreakPoint) {
          chartOptsCopy.type = 'bar';
          if (ctx.value.categories.length < 10) {
            chartOptsCopy.width = '100%';
          } else {
            chartOptsCopy.width = '1060';
          }
        } else {
          chartOptsCopy.type = 'line';
          chartOptsCopy.width = '1060';
        }
        ctx.updateOptions({ chart: chartOptsCopy });
      }, 250);
    },
    updateOptions(opts) {
      this.$refs.lineChart.updateOptions(opts);
      this.hiddenCaptions.forEach((serieName) => {
        this.$refs.lineChart.toggleSeries(serieName);
      });
    },
    applyNewChartData() {
      const xAxisOptsCopy = JSON.parse(JSON.stringify(this.options.xaxis));
      xAxisOptsCopy.labels.formatter = this.xAxisLabelsFormatter;
      xAxisOptsCopy.categories = this.value.categories;
      this.updateOptions({ xaxis: xAxisOptsCopy });
    },
    toggleSerie(serieName) {
      if (this.hiddenCaptions.includes(serieName)) {
        this.hiddenCaptions.splice(this.hiddenCaptions.indexOf(serieName), 1);
        this.track(`Por ${serieName}`, 'Desativou filtro do relatório');
      } else {
        this.hiddenCaptions.push(serieName);
        this.track(`Por ${serieName}`, 'Filtrou relatório');
      }
      this.$refs.lineChart.toggleSeries(serieName);
      this.$emit('toggle', this.hiddenCaptions);
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style lang="scss" scoped>
/deep/ {
  .apexcharts-tooltip-title {
    background: $gray !important;
    border-bottom: none !important;
    font-size: 14px !important;
    font-weight: 800 !important;
    padding: 14px 70px 0 10px !important;
    line-height: 9px !important;
    margin-bottom: 6px !important;
  }
  .apexcharts-tooltip {
    border-radius: 10px;
    border: none !important;
    -webkit-box-shadow: 0 3px 6px $alto;
    -moz-box-shadow: 0 3px 6px $alto;
    box-shadow: 0 3px 6px $alto;
    &.light {
      background: $gray;
      color: $text-light;
    }
  }
  .apexcharts-xaxis-label {
    font-size: 8px;
    line-height: 10px;
  }
  .apexcharts-tooltip-text-label {
    font-weight: 500;
  }
  .apexcharts-tooltip-text-value {
    font-weight: normal;
    margin-left: 45px;
  }
  .apexcharts-tooltip-y-group {
    padding-bottom: 0;
    line-height: 21px;
  }
  .apexcharts-tooltip-series-group {
    &.active {
      padding: 0 20px 10px 10px;
    }
  }
}

.chart-box {
  border-color: $alto !important;
  border-radius: 8px !important;
}

.box-legend {
  div {
    line-height: 20px;
    .box-legend-title {
      font-size: 17px;
      color: $text-warning;
      margin-bottom: 0.2rem;
    }
    .box-legend-subtitle {
      font-size: 16px;
      color: $dove-gray;
      margin-bottom: 0;
      font-weight: 100;
    }
  }
}

.serie-button {
  width: 126px;
  min-height: 31px;
  height: 100%;
  cursor: pointer;
  border: 1px solid;
  span {
    font-size: 12px;
    line-height: 17px;
  }
  &.hidden {
    border-color: $boulder !important;
    color: $boulder !important;
  }
  &:nth-child(1) {
    border-color: $flush-orange;
    color: $flush-orange;
  }
  &:nth-child(2) {
    border-color: $rajah;
    color: $rajah;
  }
  &:nth-child(3) {
    border-color: $copper;
    color: $copper;
  }
  &:nth-child(4) {
    border-color: $red;
    color: $red;
  }
}
</style>
